import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../api/auth-api';
import { completarOnboardingConFiscal } from './onboarding-fiscal';
import { clearAuthTokens } from '@/config/axios.config';
import { setTenant } from '@/shared/store/slices/tenant-slice';
import { sessionService } from '@/shared/services/session.service';
import {
  loginRequest,
  registerRequest,
  onboardingRequest,
  autoCompleteOnboardingRequest,
  logoutRequest,
  refreshTokenRequest,
  verifyTokenRequest,
  oauthCallbackRequest,
  forgotPasswordRequest,
  resetPasswordRequest,
  loginSuccess,
  registerSuccess,
  onboardingSuccess,
  autoCompleteOnboardingSuccess,
  logoutSuccess,
  refreshTokenSuccess,
  verifyTokenSuccess,
  oauthCallbackSuccess,
  forgotPasswordSuccess,
  resetPasswordSuccess,
  resetPasswordFailure,
  authFailure,
  clearAuth,
  LoginRequest,
  RegisterRequest,
  OnboardingRequest,
  AutoCompleteOnboardingRequest,
  RefreshTokenRequest,
  OAuthCallbackRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from './auth-slice';

// Saga para login
function* loginSaga(
  action: PayloadAction<LoginRequest>
): Generator<any, void, any> {
  try {
    const { credentials } = action.payload;

    const response: any = yield call(authApi.login, credentials);

    // GUARD: validar shape de respuesta antes de persistir/despachar success.
    // Si response.user es undefined, la persistencia se saltaba silenciosamente
    // y loginSuccess se despachaba con user:undefined dejando Redux inconsistente.
    if (!response || !response.user) {
      console.error(
        '[loginSaga] Invalid response shape - missing user field. Response was:',
        response
      );
      yield put(
        authFailure(
          'Respuesta del servidor inválida — falta campo user. Verifica el envelope de /auth/login.'
        )
      );
      return;
    }

    // Save user data + tokens for cross-domain Bearer auth
    try {
      sessionService.saveSession({
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
        redirectUrl: response.redirectUrl,
      });
    } catch (persistError) {
      console.error('[loginSaga] sessionService.saveSession failed', persistError);
      yield put(
        authFailure('No se pudo persistir la sesión. Revisa cookies/localStorage.')
      );
      return;
    }

    const tenantId = response.tenantId ?? response.user?.tenantId;

    if (tenantId) {
      const tenantName = response.user?.tenant ?? response.tenant ?? 'Tenant';

      yield put(
        setTenant({
          id: tenantId,
          name: tenantName,
          subdomain: response.tenant ?? tenantName.toLowerCase(),
          isActive: true,
          plan: {
            id: 'placeholder-plan',
            name: 'Plan asignado',
            price: 0,
            currency: 'USD',
            features: [],
            maxUsers: 0,
            maxProperties: 0,
            maxStorage: 0,
          },
          modules: [],
          settings: {
            language: 'es',
            timezone: 'UTC',
            currency: 'USD',
            dateFormat: 'DD/MM/YYYY',
            enableNotifications: true,
            enableAnalytics: true,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      );
    }

    yield put(
      loginSuccess({
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
      })
    );
  } catch (error: any) {
    yield put(
      authFailure(error.response?.data?.message || 'Error al iniciar sesión')
    );
  }
}

// Saga para registro
function* registerSaga(
  action: PayloadAction<RegisterRequest>
): Generator<any, void, any> {
  try {
    const { userData } = action.payload;
    const response: any = yield call(authApi.register, userData);

    // Save user data + tokens for cross-domain Bearer auth
    sessionService.saveSession({
      user: response.user,
      token: response.token,
      refreshToken: response.refreshToken,
      redirectUrl: response.redirectUrl,
    });

    const tenantId = response.tenantId ?? response.user?.tenantId;

    if (tenantId) {
      const tenantName = response.user?.tenant ?? response.tenant ?? 'Tenant';

      yield put(
        setTenant({
          id: tenantId,
          name: tenantName,
          subdomain: response.tenant ?? tenantName.toLowerCase(),
          isActive: true,
          plan: {
            id: 'placeholder-plan',
            name: 'Plan asignado',
            price: 0,
            currency: 'USD',
            features: [],
            maxUsers: 0,
            maxProperties: 0,
            maxStorage: 0,
          },
          modules: [],
          settings: {
            language: 'es',
            timezone: 'UTC',
            currency: 'USD',
            dateFormat: 'DD/MM/YYYY',
            enableNotifications: true,
            enableAnalytics: true,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      );
    }

    yield put(
      registerSuccess({
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
      })
    );
  } catch (error: any) {
    yield put(
      authFailure(error.response?.data?.message || 'Error al registrarse')
    );
  }
}

// Saga para onboarding
function* onboardingSaga(
  action: PayloadAction<OnboardingRequest>
): Generator<any, void, any> {
  
  try {
    const { onboardingData } = action.payload;

    // Obtener sesión actual completa
    const currentSession = sessionService.getSession();
    const currentUser = currentSession?.user;

    // GH#2107 — con los datos fiscales del alta; si el backend los rechaza, se completa sin ellos.
    const response: any = yield* completarOnboardingConFiscal(
      authApi.completeOnboarding,
      onboardingData,
      sessionStorage,
      localStorage, // la marca de «fiscal pendiente» sobrevive a cerrar la pestaña
      currentUser?.email // y queda atada al correo del alta
    );

    // Actualizar la sesión con los nuevos datos del usuario y tokens
    if (currentUser && response.user) {
      const updatedUser = {
        ...currentUser,
        ...response.user,
      };

      // Guardar nuevo token y refreshToken si están disponibles
      sessionService.saveSession({
        user: updatedUser,
        token: response.token || currentSession?.token,
        refreshToken: response.refreshToken || currentSession?.refreshToken,
        redirectUrl: response.redirectUrl || currentSession?.redirectUrl,
      });

      // Verificar que se guardó correctamente
      const savedSession = sessionService.getSession();
      if (!savedSession?.token) {
        console.warn('[Onboarding] Session save may have failed');
      }
    }

    yield put(onboardingSuccess({ user: response.user }));

  } catch (error: any) {

    const errorMessage = error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'Error al completar el onboarding';

    yield put(authFailure(errorMessage));
  }
}

// Saga para auto-completar onboarding
function* autoCompleteOnboardingSaga(
  _action: PayloadAction<AutoCompleteOnboardingRequest>
): Generator<any, void, any> {
  try {

    const response: any = yield call(authApi.autoCompleteOnboarding);


    // Actualizar la sesión con los nuevos datos del usuario
    const currentSession = sessionService.getSession();
    if (currentSession) {
      const updatedUser = {
        ...currentSession.user,
        ...response.user,
      };

      sessionService.saveSession({
        ...currentSession,
        user: updatedUser,
      });

    }

    yield put(autoCompleteOnboardingSuccess({ 
      user: response.user, 
      wasAutoCompleted: response.wasAutoCompleted 
    }));

  } catch (error: any) {
    yield put(authFailure(error.message || 'Error al auto-completar el onboarding'));
  }
}

// Saga para logout
function* logoutSaga(): Generator<any, void, any> {
  try {
    yield call(authApi.logout);
  } catch (error) {
    console.warn("Server-side logout call failed, proceeding with local logout regardless:", error);
  } finally {
    sessionService.clearSession();
    clearAuthTokens();

    yield put(logoutSuccess());
  }
}

// Saga para refresh token
function* refreshTokenSaga(
  _action: PayloadAction<RefreshTokenRequest>
): Generator<any, void, any> {
  try {
    const response: any = yield call(authApi.refreshToken);

    if (response.user) {
      sessionService.saveSession({
        user: response.user,
        token: response.token,
        refreshToken: response.refreshToken,
        redirectUrl: response.redirectUrl,
      });
    }

    yield put(
      refreshTokenSuccess({
        token: response.token,
        refreshToken: response.refreshToken,
      })
    );
  } catch (error: any) {
    sessionService.clearSession();
    yield put(clearAuth());
    clearAuthTokens();
    yield put(
      authFailure('Sesión expirada. Por favor, inicia sesión nuevamente.')
    );
  }
}

// Saga para verificar token
function* verifyTokenSaga(): Generator<any, void, any> {
  try {
    const response: any = yield call(authApi.verifyToken);

    if (response.valid && response.user) {
      yield put(verifyTokenSuccess(response.user));
    } else {
      sessionService.clearSession();
      yield put(clearAuth());
      clearAuthTokens();
    }
  } catch (error: any) {
    sessionService.clearSession();
    yield put(clearAuth());
    clearAuthTokens();
    yield put(authFailure('Token inválido'));
  }
}

// Saga para OAuth callback (Google, Facebook, etc.)
function* oauthCallbackSaga(
  action: PayloadAction<OAuthCallbackRequest>
): Generator<any, void, any> {
  try {
    const { token, refreshToken } = action.payload;

    // Obtener datos del usuario usando el token (usa axios con credentials: include)
    // El backend devuelve { user: {...}, redirectUrl: "..." }
    const response: any = yield call(authApi.getProfileWithRedirect);
    const user = response.user;
    const redirectUrl = response.redirectUrl;

    // Guardar sesión usando el servicio centralizado
    sessionService.saveSession({
      user: user,
      token: token,
      refreshToken: refreshToken,
      redirectUrl: redirectUrl,
    });

    // Configurar tenant si está disponible
    const tenantId = user?.tenantId;
    if (tenantId) {
      const tenantName = user?.tenant ?? 'Tenant';

      yield put(
        setTenant({
          id: tenantId,
          name: tenantName,
          subdomain: tenantName.toLowerCase(),
          isActive: true,
          plan: {
            id: 'placeholder-plan',
            name: 'Plan asignado',
            price: 0,
            currency: 'USD',
            features: [],
            maxUsers: 0,
            maxProperties: 0,
            maxStorage: 0,
          },
          modules: [],
          settings: {
            language: 'es',
            timezone: 'UTC',
            currency: 'USD',
            dateFormat: 'DD/MM/YYYY',
            enableNotifications: true,
            enableAnalytics: true,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      );
    }

    // Dispatch success con los datos del usuario
    yield put(
      oauthCallbackSuccess({
        user: user,
        token: null, // Con HTTP-only cookies, no necesitamos el token en el store
        refreshToken: null,
      })
    );
  } catch (error: any) {
    yield put(
      authFailure(error.response?.data?.message || 'Error al procesar autenticación OAuth')
    );
  }
}

// Watcher sagas
export function* watchLogin() {
  yield takeLatest(loginRequest.type, loginSaga);
}

export function* watchRegister() {
  yield takeLatest(registerRequest.type, registerSaga);
}

export function* watchOnboarding() {
  yield takeLatest(onboardingRequest.type, onboardingSaga);
}

export function* watchAutoCompleteOnboarding() {
  yield takeLatest(autoCompleteOnboardingRequest.type, autoCompleteOnboardingSaga);
}

export function* watchLogout() {
  yield takeLatest(logoutRequest.type, logoutSaga);
}

export function* watchRefreshToken() {
  yield takeLatest(refreshTokenRequest.type, refreshTokenSaga);
}

export function* watchVerifyToken() {
  yield takeLatest(verifyTokenRequest.type, verifyTokenSaga);
}

export function* watchOAuthCallback() {
  yield takeLatest(oauthCallbackRequest.type, oauthCallbackSaga);
}

// Saga para forgot password
function* forgotPasswordSaga(
  action: PayloadAction<ForgotPasswordRequest>
): Generator<any, void, any> {
  try {
    const { email } = action.payload;
    yield call(authApi.requestPasswordReset, email);
    yield put(forgotPasswordSuccess());
  } catch (error: any) {
    yield put(
      authFailure(error.response?.data?.message || 'Error al solicitar recuperación de contraseña')
    );
  }
}

export function* watchForgotPassword() {
  yield takeLatest(forgotPasswordRequest.type, forgotPasswordSaga);
}

// Saga para reset password
function* resetPasswordSaga(
  action: PayloadAction<ResetPasswordRequest>
): Generator<any, void, any> {
  try {
    const { email, code, newPassword } = action.payload;
    yield call(authApi.resetPassword, email, code, newPassword);
    yield put(resetPasswordSuccess());
  } catch (error: any) {
    // GH#2716: `resetPasswordFailure` y no `authFailure`: este último apaga `forgotPasswordSent` y la vista
    // «Nueva contraseña» desaparecía con cualquier error del API.
    yield put(
      resetPasswordFailure(error.response?.data?.message || 'Error al restablecer la contraseña')
    );
  }
}

export function* watchResetPassword() {
  yield takeLatest(resetPasswordRequest.type, resetPasswordSaga);
}

// Root saga para security
export function* securitySaga() {
  yield takeLatest(loginRequest.type, loginSaga);
  yield takeLatest(registerRequest.type, registerSaga);
  yield takeLatest(onboardingRequest.type, onboardingSaga);
  yield takeLatest(autoCompleteOnboardingRequest.type, autoCompleteOnboardingSaga);
  yield takeLatest(logoutRequest.type, logoutSaga);
  yield takeLatest(refreshTokenRequest.type, refreshTokenSaga);
  yield takeLatest(verifyTokenRequest.type, verifyTokenSaga);
  yield takeLatest(oauthCallbackRequest.type, oauthCallbackSaga);
  yield takeLatest(forgotPasswordRequest.type, forgotPasswordSaga);
  yield takeLatest(resetPasswordRequest.type, resetPasswordSaga);
}
